import {
  boreLitresPerMetre,
  COPPER_CLIP_SPACING_HORIZONTAL_M,
  coilsFor,
  PEX_BARRIER_COILS_M,
  PEX_COIL_SOURCE,
  PEX_LENGTH_SOURCE,
} from "./pipe-stock-data.js";
import {
  BUFFER_ROLE_NAMES,
  DEFAULT_PIPE_MATERIAL,
  FITTING_NAMES,
  fittingSizeLabel,
  litresText,
  PANEL_TYPE_ALIASES,
  PIPE_DIAMETERS_MM,
  PIPE_MATERIAL_NAMES,
  PLAN_PIPE_MATERIALS,
  pipeMaterialOf,
  pipeSizeLabel,
  RADIATOR_PLAN_RANGE,
  radiatorLabel,
  vesselCaption,
  type FittingKind,
  type PipeCircuit,
  type PipeDiameterMm,
  type PlanObjectPoint,
  type PlanObjectShape,
  type PlanPipeMaterial,
} from "./plan-objects.js";

/**
 * The stock report — the bill of materials a van is loaded from — derived from
 * the designer's floor-plan objects (migration 0032), the org's own stock
 * catalogue, and the per-survey adjustments (migration 0033).
 *
 * Asked for on 2026-09-10: "once the design is complete from our side … we
 * should be able to have all of the stock report, like, how much stock, how
 * much connectors … he knows exactly how much stock he needs to take, how much
 * pipeline is going to be there, how many connectors".
 *
 * EVERY QUANTITY SAYS WHERE IT CAME FROM. Each line carries a `source` label and
 * a `basis` sentence. The labels follow the research note
 * `docs/research/installer-pack-2026-09-10.md` (§2 "Default stock catalogue"):
 *
 *   plan        counted straight off the drawing
 *   design      from the design decisions (migration 0021)
 *   R           a rule read in a standard or a manufacturer's own document
 *   V           a vendor's or distributor's page (what they claim)
 *   I           an inference — arithmetic on the drawing, not a stated rule
 *   RoT         a rule of thumb, not in any standard
 *   assumption  a default figure Scannix had to pick; shown so it gets checked
 *   catalogue   the organisation's own stock catalogue rule
 *   manual      a line somebody typed in for this job
 *
 * Nothing MCS publishes requires a materials list (MIS 3005-D cl. 3.8.1 lists
 * what the installer "shall be provided in writing with", and stock is not on
 * it — research, key finding 1). So these are estimating rules, and every one
 * of them is visible and overridable rather than buried.
 *
 * WHAT A 2D PLAN CANNOT KNOW. Pipe lengths on the plan are plan-view only: the
 * vertical drop to each radiator and the risers between floors are not drawn
 * (research §1, "Gaps"). The report adds an editable drops allowance per
 * radiator and per-job extra metres so the undercount is corrected by a figure
 * a person can see and change, not hidden.
 *
 * MATERIALS (2026-09-11). A run is copper, PEX barrier or multilayer
 * (`PlanPipeMaterial`; a run drawn before then is copper). Copper is bought in
 * straight lengths; PEX barrier in the coils its maker lists for the size
 * (`pipe-stock-data.ts`), or in straight lengths where no coil is listed. Pipe,
 * elbows and couplings are counted per size AND material, because a push-fit
 * PEX elbow and an end-feed copper one are not the same thing on the van.
 * Copper keeps the keys it had before materials existed (`pipe:15`), so a
 * quantity somebody overrode then still applies.
 *
 * SYSTEM VOLUME (2026-09-11). The water the heating circuit holds, from what
 * is on record: pipe bore per metre, cylinder coils and buffers on the plan,
 * and the litres a person adds (the heat pump's own content, radiators). What
 * is not on record is listed as NOT INCLUDED, never estimated. Inhibitor and
 * cleaner are dosed from it by a catalogue rule ("per 100 L of system water",
 * from the product's label), and glycol at the concentration set here.
 *
 * PURE. No clock, no I/O, no randomness: the same plan and settings always
 * produce the same report, so the installer pack that prints it hashes stably.
 */

// ------------------------------------------------------------------ labels --

export const STOCK_SOURCES = [
  "plan",
  "design",
  "R",
  "V",
  "I",
  "RoT",
  "assumption",
  "catalogue",
  "manual",
] as const;
export type StockSource = (typeof STOCK_SOURCES)[number];

/**
 * Groups in the order a van is loaded and a pick list is walked: the big plant
 * first, then emitters, then the pipe, what joins it, what holds it up, what
 * wraps it, and the consumables.
 */
export const STOCK_GROUPS = [
  "plant",
  "radiators",
  "pipe",
  "fittings",
  "fixings",
  "insulation",
  "water-treatment",
  "electrical",
  "other",
] as const;
export type StockGroup = (typeof STOCK_GROUPS)[number];

export const STOCK_GROUP_TITLES: Readonly<Record<StockGroup, string>> = {
  plant: "Heat pump and plant",
  radiators: "Radiators and valves",
  pipe: "Pipe",
  fittings: "Fittings",
  fixings: "Clips and fixings",
  insulation: "Pipe insulation",
  "water-treatment": "Water treatment",
  electrical: "Electrical",
  other: "Other",
};

// ---------------------------------------------------------------- settings --

export interface ExtraPipe {
  readonly diameterMm: PipeDiameterMm;
  /** Metres of pipe the plan does not show: risers, the run out to the unit. */
  readonly metres: number;
  /** Absent: copper, as every extra was before materials existed. */
  readonly material?: PlanPipeMaterial;
}

/** Water the plan cannot count, typed in with where it came from — "Heat pump (datasheet)", 3 L. */
export interface VolumeExtra {
  readonly label: string;
  readonly litres: number;
}

/** The per-survey knobs. Every one has a default that is shown and labelled. */
export interface StockSettings {
  /**
   * Metres of pipe added per radiator for the vertical drops a plan cannot
   * show — BOTH tails together (flow and return), split evenly between them.
   */
  readonly dropPerRadiatorM: number;
  /** The pipe size the drops are counted in. Radiator tails are usually 15 mm. */
  readonly dropDiameterMm: PipeDiameterMm;
  /** What the drops are made of. */
  readonly dropMaterial: PlanPipeMaterial;
  /** Per-job extra metres by diameter — risers between floors, the wall run. */
  readonly extraPipe: readonly ExtraPipe[];
  /** Added to the pipe and insulation metres before they are rounded to stock. */
  readonly wastagePct: number;
  /** The straight length pipe is bought in, metres — copper, and plastic with no coil listed. */
  readonly stockLengthM: number;
  /**
   * The coil plastic pipe is bought in, metres. 0 means "the coil lengths the
   * maker lists for the size", made up into the fewest coils that cover it.
   */
  readonly coilLengthM: number;
  /** Share of the pipe that gets lagged, per cent. */
  readonly insulationCoveragePct: number;
  /** Glycol (propylene) in the system water, per cent by volume; 0 is a water-only system. */
  readonly glycolConcentrationPct: number;
  /** Water the plan cannot count: the heat pump's own, radiators', a manifold's. */
  readonly volumeExtras: readonly VolumeExtra[];
}

/**
 * The defaults, each with the reason it is what it is (also printed, see
 * `STOCK_SETTING_BASIS`).
 */
export const DEFAULT_STOCK_SETTINGS: StockSettings = {
  dropPerRadiatorM: 1,
  dropDiameterMm: 15,
  dropMaterial: DEFAULT_PIPE_MATERIAL,
  extraPipe: [],
  wastagePct: 10,
  stockLengthM: 3,
  coilLengthM: 0,
  insulationCoveragePct: 100,
  glycolConcentrationPct: 0,
  volumeExtras: [],
};

export const STOCK_SETTING_BASIS: Readonly<Record<keyof StockSettings, { source: StockSource; text: string }>> = {
  dropPerRadiatorM: {
    source: "assumption",
    text:
      "Assumed 1.0 m per radiator (0.5 m each tail: floor void to valve plus the turn). " +
      "A 2D plan has no vertical length; change it for rooms fed from a ceiling.",
  },
  dropDiameterMm: {
    source: "assumption",
    text: "Assumed 15 mm — the usual radiator tail size. Change it for microbore.",
  },
  dropMaterial: {
    source: "assumption",
    text: "Assumed copper, the plan's default material. Change it where the tails are plastic.",
  },
  extraPipe: {
    source: "assumption",
    text:
      "None by default. Add risers between floors and the run through the wall to the " +
      "outdoor unit here — the plan does not draw them.",
  },
  wastagePct: {
    source: "RoT",
    text: "10% — generic estimating guidance quotes 5–10% (research §2, from mostly US sources).",
  },
  stockLengthM: {
    source: "R",
    text:
      "3 m — copper tube is sold in 3 m lengths (6 m also sold) per the Mueller copper guide (research §2); " +
      "Yorkshire's Yorkex R250 table lists 3 m for 10–35 mm. Plastic pipe with no coil listed is counted in these lengths too.",
  },
  coilLengthM: {
    source: "V",
    text:
      "0 = the coils JG Speedfit lists for PEX barrier pipe (10 mm: 25/50/100 m; 15 mm: 25/50/100/120/150 m; 22 mm: 25/50 m — " +
      "data sheet 15TB-001-0821), made up into the fewest coils that cover the metres. 28 mm is sold in 3 m and 6 m lengths only. " +
      "Set a length to buy every plastic run in that coil instead — and for multilayer, whose coils are not on record.",
  },
  insulationCoveragePct: {
    source: "RoT",
    text:
      "100% — common practice lags every primary at 19–25 mm. The legal minimum (AD L " +
      "4.24–4.26) is primaries outside heated space, all DHW primaries and 1 m from the cylinder.",
  },
  glycolConcentrationPct: {
    source: "R",
    text:
      "0 = no glycol: frost protection by anti-freeze valves (the catalogue's default). EST's toolkit: a 25% glycol mix " +
      "protects to about −10 °C, and glycol cuts the heat pump's output (its example: 7.01 → 6.42 kW) [R].",
  },
  volumeExtras: {
    source: "manual",
    text:
      "Water the plan cannot count — the heat pump's internal content and each radiator's, from their datasheets. " +
      "Nothing is assumed for them.",
  },
};

/**
 * Maximum horizontal spacing between clips on copper tube, metres, by nominal
 * outside diameter — `COPPER_CLIP_SPACING_HORIZONTAL_M` in pipe-stock-data.ts
 * (Mueller Table 10 via research §2, 35 mm added 2026-09-11). Kept under this
 * name for the report's readers.
 */
export const CLIP_SPACING_HORIZONTAL_M = COPPER_CLIP_SPACING_HORIZONTAL_M;

/** A bend sharper than this, degrees, needs a fitting. Shallower is a kink in the drawing. */
export const MIN_BEND_DEG = 10;
/** A drawn fitting this close to a bend or a run, metres, is taken to be on it. */
export const FITTING_SNAP_M = 0.15;

// --------------------------------------------------------------- catalogue --

export const STOCK_RULE_KINDS = [
  "per-job",
  "per-radiator",
  "per-heat-pump",
  "per-metre-of-pipe",
  "per-100-litres-of-system-water",
] as const;
export type StockRuleKind = (typeof STOCK_RULE_KINDS)[number];

export interface StockQuantityRule {
  readonly kind: StockRuleKind;
  /** How many per job / radiator / heat pump / metre / 100 litres. */
  readonly quantity: number;
}

/** One item in an organisation's stock catalogue, as the report reads it. */
export interface StockCatalogueEntry {
  readonly id: string;
  readonly name: string;
  readonly unit: string;
  readonly category: StockGroup;
  readonly code: string | null;
  /** Null: not added automatically — offered to add by hand. */
  readonly rule: StockQuantityRule | null;
  readonly basis: string | null;
  readonly source: StockSource | null;
  readonly active: boolean;
}

// ------------------------------------------------------------- adjustments --

export interface StockLineAdjustment {
  /** Replaces the derived figure. The derived one stays visible beside it. */
  readonly quantity?: number;
  readonly excluded?: boolean;
  readonly note?: string;
}

export interface StockManualLine {
  readonly id: string;
  readonly group: StockGroup;
  readonly item: string;
  readonly spec: string;
  readonly quantity: number;
  readonly unit: string;
  readonly note: string;
}

export interface StockAdjustments {
  /** Only the settings somebody changed; the rest come from the defaults. */
  readonly settings: Partial<StockSettings>;
  readonly lines: Readonly<Record<string, StockLineAdjustment>>;
  readonly manualLines: readonly StockManualLine[];
}

export const EMPTY_STOCK_ADJUSTMENTS: StockAdjustments = {
  settings: {},
  lines: {},
  manualLines: [],
};

// ------------------------------------------------------------------ report --

/** A plan object as the report reads it: its shape plus where it sits. */
export type StockPlanObject = PlanObjectShape & {
  readonly id: string;
  readonly storey: number;
  readonly label: string | null;
};

export interface StockDesignFacts {
  readonly heatPump: { readonly manufacturer: string; readonly model: string } | null;
  readonly cylinder:
    | { readonly manufacturer: string; readonly model: string; readonly volumeLitres: number | null }
    | null;
}

export interface StockReportInput {
  readonly objects: readonly StockPlanObject[];
  readonly catalogue?: readonly StockCatalogueEntry[];
  readonly adjustments?: StockAdjustments;
  readonly design?: StockDesignFacts;
}

export interface StockLine {
  /** Stable across recomputation, so an override survives the plan changing. */
  readonly key: string;
  readonly group: StockGroup;
  readonly item: string;
  readonly spec: string;
  /** What the rules give. Null where nothing can be derived (a manual line). */
  readonly derivedQuantity: number | null;
  /** What goes on the van: the override if there is one, else the derived figure. */
  readonly quantity: number;
  readonly unit: string;
  /** How the derived figure was reached, in words and numbers. */
  readonly basis: string;
  readonly source: StockSource;
  /** Extra measure worth showing beside the quantity — "7.52 m to order". */
  readonly measure?: string;
  readonly origin: "derived" | "catalogue" | "manual";
  readonly overridden: boolean;
  readonly excluded: boolean;
  readonly note: string | null;
}

export interface PipeRunSummary {
  readonly id: string;
  readonly storey: number;
  readonly label: string | null;
  readonly circuit: PipeCircuit;
  readonly diameterMm: PipeDiameterMm;
  readonly material: PlanPipeMaterial;
  readonly lengthM: number;
  readonly legs: number;
  readonly bends: number;
  /** Bends with no elbow drawn on them — each gets a derived elbow. */
  readonly uncoveredBends: number;
  readonly derivedCouplings: number;
  /** Null where no clip spacing is known for the size and material. */
  readonly clips: number | null;
}

/** How a size and material is bought: straight lengths, or coils. */
export interface PipeStockPieces {
  readonly unit: "length" | "coil";
  /** Each piece length and how many — [{ lengthM: 50, count: 1 }, { lengthM: 25, count: 1 }]. */
  readonly pieces: readonly { readonly lengthM: number; readonly count: number }[];
}

export interface PipeDiameterTotal {
  readonly diameterMm: PipeDiameterMm;
  readonly material: PlanPipeMaterial;
  readonly flowM: number;
  readonly returnM: number;
  readonly dropsM: number;
  readonly extraM: number;
  /** flow + return + drops + extra, before wastage. */
  readonly totalM: number;
  /** totalM with wastage added. */
  readonly orderM: number;
  /** How many pieces are bought — lengths, or coils. */
  readonly lengths: number;
  readonly stock: PipeStockPieces;
}

/** One part of the system water volume. `litres` is null for a part that is on the job but not on record. */
export interface SystemVolumeLine {
  readonly key: string;
  readonly component: string;
  readonly litres: number | null;
  readonly basis: string;
  readonly source: StockSource;
}

export interface SystemVolume {
  readonly lines: readonly SystemVolumeLine[];
  /** The sum of every line with litres — a floor, when anything is not included. */
  readonly knownLitres: number;
  /** The components on the job whose water is not counted. */
  readonly notIncluded: readonly string[];
}

export interface StockGroupView {
  readonly group: StockGroup;
  readonly title: string;
  readonly lines: readonly StockLine[];
  readonly notes: readonly string[];
}

export interface StockReport {
  /** The settings in force: defaults with the survey's changes applied. */
  readonly settings: StockSettings;
  readonly groups: readonly StockGroupView[];
  readonly lines: readonly StockLine[];
  readonly pipeRuns: readonly PipeRunSummary[];
  readonly pipeTotals: readonly PipeDiameterTotal[];
  readonly systemVolume: SystemVolume;
  /** The glycol the settings ask for, or null for a water-only system. */
  readonly glycol: { readonly concentrationPct: number; readonly litres: number } | null;
  readonly counts: {
    readonly radiators: number;
    readonly heatPumps: number;
    readonly pipeRuns: number;
    readonly cylinders: number;
    readonly buffers: number;
  };
  readonly warnings: readonly string[];
  /** Adjustments whose line no longer exists (the object was taken off the plan). */
  readonly unmatchedAdjustmentKeys: readonly string[];
}

// ------------------------------------------------------------ arithmetic --

/**
 * Rounds away floating-point dust before a ceiling, so 3 × 1.1 ÷ 3.3 is 1 and
 * not 2. Micrometre precision is far below anything a tape measures.
 */
function clean(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

export function ceilClean(value: number): number {
  return Math.ceil(clean(value));
}

function fmt(value: number, decimals = 2): string {
  return clean(value).toFixed(decimals);
}

/** "1", "1.5" — a quantity as a person writes it. */
function qty(value: number): string {
  const c = clean(value);
  return Number.isInteger(c) ? String(c) : String(Number(c.toFixed(3)));
}

// --------------------------------------------------------------- geometry --

export function runLengthM(points: readonly PlanObjectPoint[]): number {
  let length = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1] as PlanObjectPoint;
    const b = points[i] as PlanObjectPoint;
    length += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return length;
}

/** The straight legs of a run, metres, skipping zero-length ones (a doubled click). */
export function runLegsM(points: readonly PlanObjectPoint[]): number[] {
  const legs: number[] = [];
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1] as PlanObjectPoint;
    const b = points[i] as PlanObjectPoint;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length > 1e-6) legs.push(length);
  }
  return legs;
}

/**
 * The interior points of a run where it turns by at least `minTurnDeg` —
 * where a real pipe needs a fitting. Collinear points (a run drawn with an
 * extra click along a straight wall) are not bends.
 */
export function runBends(
  points: readonly PlanObjectPoint[],
  minTurnDeg = MIN_BEND_DEG,
): PlanObjectPoint[] {
  // Drop repeated points first so a doubled click is not read as a 180° turn.
  const pts: PlanObjectPoint[] = [];
  for (const p of points) {
    const last = pts[pts.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1e-6) pts.push(p);
  }
  const bends: PlanObjectPoint[] = [];
  for (let i = 1; i < pts.length - 1; i += 1) {
    const a = pts[i - 1] as PlanObjectPoint;
    const b = pts[i] as PlanObjectPoint;
    const c = pts[i + 1] as PlanObjectPoint;
    const h1 = Math.atan2(b.y - a.y, b.x - a.x);
    const h2 = Math.atan2(c.y - b.y, c.x - b.x);
    let turn = Math.abs(h2 - h1) * (180 / Math.PI);
    if (turn > 180) turn = 360 - turn;
    if (turn >= minTurnDeg) bends.push(b);
  }
  return bends;
}

function distanceToSegment(p: PlanObjectPoint, a: PlanObjectPoint, b: PlanObjectPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared));
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
}

export function distanceToRunM(p: PlanObjectPoint, points: readonly PlanObjectPoint[]): number {
  let best = Infinity;
  for (let i = 1; i < points.length; i += 1) {
    best = Math.min(best, distanceToSegment(p, points[i - 1] as PlanObjectPoint, points[i] as PlanObjectPoint));
  }
  return best;
}

/**
 * Clips on one run: each straight leg of length L needs ceil(L ÷ spacing)
 * spans, and a run of n spans has n + 1 clips — which puts a clip at each end
 * and at every bend (shared by the two legs meeting there). The spacing is
 * Mueller Table 10's horizontal figure [R]; the count formula is [I].
 */
export function clipsForRun(legs: readonly number[], spacingM: number): number {
  if (legs.length === 0) return 0;
  return legs.reduce((sum, leg) => sum + ceilClean(leg / spacingM), 0) + 1;
}

/** A place where runs of one circuit meet, and the runs that meet there. */
export interface PipeJoint {
  readonly at: PlanObjectPoint;
  readonly storey: number;
  readonly circuit: PipeCircuit;
  /** Each run that meets here, with its size — an end, or a branch onto its middle. */
  readonly runs: readonly { readonly id: string; readonly diameterMm: PipeDiameterMm; readonly material: PlanPipeMaterial }[];
}

type PipeObject = Extract<StockPlanObject, { kind: "pipe" }>;

/**
 * Where drawn runs meet: an END of one run within `FITTING_SNAP_M` of another
 * run of the same circuit on the same storey — its end (runs drawn end to
 * end) or anywhere along it (a branch). Flow and return are never joined.
 * Several ends at one spot (a tee) are one joint.
 */
export function pipeJoints(pipes: readonly PipeObject[]): PipeJoint[] {
  type Draft = { at: PlanObjectPoint; storey: number; circuit: PipeCircuit; ids: Set<string> };
  const drafts: Draft[] = [];
  for (const run of pipes) {
    const pts = run.geometry.points;
    const ends = [pts[0], pts[pts.length - 1]].filter((p): p is PlanObjectPoint => p !== undefined);
    for (const end of ends) {
      const touching = pipes.filter(
        (other) =>
          other.id !== run.id &&
          other.storey === run.storey &&
          other.spec.circuit === run.spec.circuit &&
          distanceToRunM(end, other.geometry.points) <= FITTING_SNAP_M,
      );
      if (touching.length === 0) continue;
      const near = drafts.find(
        (d) =>
          d.storey === run.storey &&
          d.circuit === run.spec.circuit &&
          Math.hypot(d.at.x - end.x, d.at.y - end.y) <= 2 * FITTING_SNAP_M,
      );
      const target = near ?? { at: end, storey: run.storey, circuit: run.spec.circuit, ids: new Set<string>() };
      if (!near) drafts.push(target);
      target.ids.add(run.id);
      for (const t of touching) target.ids.add(t.id);
    }
  }
  const byId = new Map(pipes.map((p) => [p.id, p]));
  return drafts.map((d) => ({
    at: d.at,
    storey: d.storey,
    circuit: d.circuit,
    runs: [...d.ids]
      .map((id) => byId.get(id)!)
      .map((p) => ({ id: p.id, diameterMm: p.spec.diameterMm, material: pipeMaterialOf(p.spec) })),
  }));
}

/**
 * The reducers a joint needs: one for each run meeting it that is SMALLER than
 * the largest run there, reducing from that largest size [I]. Two 22 mm runs
 * and a 15 mm branch at a tee → one 22 × 15 reducer (or a reducing tee).
 */
export function reducersAtJoint(joint: PipeJoint): { readonly from: PipeDiameterMm; readonly to: PipeDiameterMm }[] {
  const largest = Math.max(...joint.runs.map((r) => r.diameterMm)) as PipeDiameterMm;
  return joint.runs.filter((r) => r.diameterMm < largest).map((r) => ({ from: largest, to: r.diameterMm }));
}

// -------------------------------------------------------------- settings --

function mergeSettings(partial: Partial<StockSettings> | undefined): StockSettings {
  return { ...DEFAULT_STOCK_SETTINGS, ...(partial ?? {}) };
}

// ------------------------------------------------------------- the report --

interface DraftLine {
  readonly key: string;
  readonly group: StockGroup;
  readonly item: string;
  readonly spec: string;
  readonly derivedQuantity: number | null;
  readonly unit: string;
  readonly basis: string;
  readonly source: StockSource;
  readonly measure?: string;
  readonly origin: StockLine["origin"];
}

const DIAMETER_ORDER = new Map<number, number>(PIPE_DIAMETERS_MM.map((d, i) => [d, i]));
const byDiameter = (a: number, b: number) => (DIAMETER_ORDER.get(a) ?? a) - (DIAMETER_ORDER.get(b) ?? b);
const MATERIAL_ORDER = new Map<string, number>(PLAN_PIPE_MATERIALS.map((m, i) => [m, i]));

/** Copper's key is the one it had before materials existed ("15"); others add the material ("pex-barrier:15"). */
function sizeKey(material: PlanPipeMaterial, d: number): string {
  return material === "copper" ? String(d) : `${material}:${d}`;
}

/** Every (material, size) pair in `items`, materials in vocabulary order, then sizes ascending. */
function sizesOf<T extends { readonly material: PlanPipeMaterial; readonly diameterMm: PipeDiameterMm }>(
  items: readonly T[],
): { readonly material: PlanPipeMaterial; readonly diameterMm: PipeDiameterMm }[] {
  const seen = new Map<string, { material: PlanPipeMaterial; diameterMm: PipeDiameterMm }>();
  for (const i of items) seen.set(sizeKey(i.material, i.diameterMm), { material: i.material, diameterMm: i.diameterMm });
  return [...seen.values()].sort(
    (a, b) => (MATERIAL_ORDER.get(a.material) ?? 0) - (MATERIAL_ORDER.get(b.material) ?? 0) || byDiameter(a.diameterMm, b.diameterMm),
  );
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/** How a (material, size) is bought, and the sentence that says why. */
function stockPieces(
  material: PlanPipeMaterial,
  d: PipeDiameterMm,
  orderM: number,
  settings: StockSettings,
): { stock: PipeStockPieces; why: string; source: StockSource; warning?: string } {
  const lengths = (why: string, source: StockSource, warning?: string) => {
    const count = orderM > 0 ? ceilClean(orderM / settings.stockLengthM) : 0;
    return {
      stock: { unit: "length" as const, pieces: count > 0 ? [{ lengthM: settings.stockLengthM, count }] : [] },
      why: `÷ ${qty(settings.stockLengthM)} m lengths, rounded up = ${count}${why}`,
      source,
      ...(warning ? { warning } : {}),
    };
  };
  if (material === "copper") return lengths("", "I");

  const listed = material === "pex-barrier" ? PEX_BARRIER_COILS_M[d] : undefined;
  const coilSizes = settings.coilLengthM > 0 ? [settings.coilLengthM] : (listed ?? []);
  if (coilSizes.length === 0) {
    if (material === "pex-barrier") {
      return lengths(
        ` — no ${d} mm coil is listed (${PEX_LENGTH_SOURCE.document}: 28 mm in 3 m and 6 m lengths; 35 mm not in the range) [V]`,
        "V",
      );
    }
    return lengths(
      " — no multilayer coil or length is on record; counted in straight lengths [assumption]",
      "assumption",
      `Multilayer pipe is counted in ${qty(settings.stockLengthM)} m lengths because no coil size is on record — set the coil length under Allowances, or override the pipe lines.`,
    );
  }
  const coils = coilsFor(orderM, coilSizes);
  const counts = new Map<number, number>();
  for (const c of coils) counts.set(c, (counts.get(c) ?? 0) + 1);
  const pieces = [...counts.entries()].sort((a, b) => b[0] - a[0]).map(([lengthM, count]) => ({ lengthM, count }));
  const made = pieces.map((p) => `${p.count} × ${qty(p.lengthM)} m`).join(" + ");
  const total = coils.reduce((s, c) => s + c, 0);
  return {
    stock: { unit: "coil", pieces },
    why:
      settings.coilLengthM > 0
        ? `in ${qty(settings.coilLengthM)} m coils (set under Allowances): ${made || "none"}`
        : `made up from the ${d} mm coils JG Speedfit lists (${(listed ?? []).map((l) => `${l} m`).join(", ")} — ${PEX_COIL_SOURCE.document} [V]), ` +
          `the shortest total that covers it in the fewest coils: ${made || "none"}${total > 0 ? ` = ${qty(total)} m` : ""}`,
    source: settings.coilLengthM > 0 ? "assumption" : "V",
  };
}

/** The joint length a run is bought in: a straight length for copper, a coil for plastic. */
function jointLengthM(material: PlanPipeMaterial, d: PipeDiameterMm, settings: StockSettings): number {
  if (material === "copper") return settings.stockLengthM;
  if (settings.coilLengthM > 0) return settings.coilLengthM;
  const listed = material === "pex-barrier" ? PEX_BARRIER_COILS_M[d] : undefined;
  return listed && listed.length > 0 ? Math.max(...listed) : settings.stockLengthM;
}

/**
 * The stock report for one survey. See the module header for what each label
 * means and `docs/research/installer-pack-2026-09-10.md` §2 for the sources.
 */
export function computeStockReport(input: StockReportInput): StockReport {
  const settings = mergeSettings(input.adjustments?.settings);
  const adjustments = input.adjustments ?? EMPTY_STOCK_ADJUSTMENTS;
  const drafts: DraftLine[] = [];
  const warnings: string[] = [];
  const notes: Partial<Record<StockGroup, string[]>> = {};
  const note = (group: StockGroup, text: string) => {
    (notes[group] ??= []).push(text);
  };

  const heatPumps = input.objects.filter((o) => o.kind === "heat-pump");
  const radiators = input.objects.filter((o) => o.kind === "radiator");
  const pipes = input.objects.filter((o): o is PipeObject => o.kind === "pipe");
  const fittings = input.objects.filter((o) => o.kind === "fitting");
  const cylinders = input.objects.filter((o) => o.kind === "cylinder");
  const buffers = input.objects.filter((o) => o.kind === "buffer");

  // ---- plant: heat pump(s) and cylinder ----------------------------------

  const hpGroups = new Map<string, { name: string; spec: string; count: number }>();
  for (const hp of heatPumps) {
    const key = `heat-pump:${hp.spec.catalogueId ?? slug(hp.spec.name)}`;
    const found = hpGroups.get(key);
    if (found) found.count += 1;
    else
      hpGroups.set(key, {
        name: hp.spec.name,
        spec: `${hp.spec.name} — ${hp.spec.widthMm} W × ${hp.spec.depthMm} D × ${hp.spec.heightMm} H mm${
          hp.spec.catalogueId === null ? " (dimensions entered by hand)" : ""
        }`,
        count: 1,
      });
  }
  for (const [key, g] of hpGroups) {
    drafts.push({
      key,
      group: "plant",
      item: "Heat pump outdoor unit",
      spec: g.spec,
      derivedQuantity: g.count,
      unit: "ea",
      basis: `${g.count} drawn on the plan.`,
      source: "plan",
      origin: "derived",
    });
  }

  const designHp = input.design?.heatPump ?? null;
  const designHpName = designHp ? `${designHp.manufacturer} ${designHp.model}`.trim() : "";
  if (heatPumps.length === 0 && designHpName) {
    drafts.push({
      key: "heat-pump:design",
      group: "plant",
      item: "Heat pump outdoor unit",
      spec: designHpName,
      derivedQuantity: 1,
      unit: "ea",
      basis: "Not drawn on the plan; taken from the design decisions.",
      source: "design",
      origin: "derived",
    });
  } else if (heatPumps.length === 0) {
    warnings.push("No heat pump on the plan and none in the design decisions.");
  } else if (designHp && designHp.model.trim()) {
    // Letters and digits only, so "7kW" and "7 kW" agree.
    const squash = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, "");
    const model = squash(designHp.model);
    const mismatch = heatPumps.some((hp) => !squash(hp.spec.name).includes(model));
    if (mismatch) {
      warnings.push(
        `The plan's heat pump (${[...hpGroups.values()].map((g) => g.name).join(", ")}) does not name the ` +
          `design decisions' model (${designHpName}). Check which one is being fitted.`,
      );
    }
  }
  const heatPumpCount = heatPumps.length > 0 ? heatPumps.length : designHpName ? 1 : 0;

  // The cylinder: the design decisions name the product (MIS 3005-D 3.5); a
  // cylinder drawn on the plan says where it goes and how big. With both, the
  // design's is the one bought and the drawing is checked against it; with
  // only a drawing, the drawing's nominal size is listed to be confirmed.
  const cylinder = input.design?.cylinder ?? null;
  const cylinderName = cylinder ? `${cylinder.manufacturer} ${cylinder.model}`.trim() : "";
  if (cylinder && cylinderName) {
    drafts.push({
      key: "cylinder:design",
      group: "plant",
      item: "Hot water cylinder",
      spec: `${cylinderName}${cylinder.volumeLitres ? `, ${cylinder.volumeLitres} litres` : ""}`,
      derivedQuantity: 1,
      unit: "ea",
      basis:
        "From the design decisions (MIS 3005-D 3.5 sizing)." +
        (cylinders.length > 0 ? ` The cylinder drawn on the plan is this one — counted once.` : ""),
      source: "design",
      origin: "derived",
    });
    if (cylinders.length > 1) {
      warnings.push(
        `${cylinders.length} cylinders are drawn on the plan but the design decisions name one (${cylinderName}). Check which is being fitted.`,
      );
    }
    const designLitres = cylinder.volumeLitres;
    const off = cylinders.filter((c) => designLitres !== null && Math.abs(c.spec.volumeLitres - designLitres) > 0.5);
    if (off.length > 0 && designLitres !== null) {
      warnings.push(
        `The plan's cylinder is drawn at ${off.map((c) => `${litresText(c.spec.volumeLitres)} L`).join(", ")} but the design ` +
          `decisions name ${litresText(designLitres)} L (${cylinderName}). Redraw it at the chosen size so its footprint is right.`,
      );
    }
  } else if (cylinders.length > 0) {
    const groups = new Map<string, { spec: string; count: number; generic: boolean }>();
    for (const c of cylinders) {
      const key = `cylinder:plan:${c.spec.genericId ?? slug(`${c.spec.name}-${c.spec.volumeLitres}`)}`;
      const found = groups.get(key);
      if (found) found.count += 1;
      else
        groups.set(key, {
          spec: `${c.spec.name}, ${litresText(c.spec.volumeLitres)} litres, ${vesselCaption(c.spec)}${
            c.spec.genericId !== null ? " — nominal, confirm with the chosen product" : " (entered by hand)"
          }`,
          count: 1,
          generic: c.spec.genericId !== null,
        });
    }
    for (const [key, g] of [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))) {
      drafts.push({
        key,
        group: "plant",
        item: "Hot water cylinder",
        spec: g.spec,
        derivedQuantity: g.count,
        unit: "ea",
        basis: `${g.count} drawn on the plan. No cylinder is named in the design decisions yet — record the product there for the design brief (MIS 3005-D 3.8.1(e)).`,
        source: "plan",
        origin: "derived",
      });
    }
  } else {
    warnings.push("No cylinder in the design decisions or on the plan — add one there, or as a manual line.");
  }

  const bufferGroups = new Map<string, { item: string; spec: string; count: number }>();
  for (const b of buffers) {
    const key = `buffer:${b.spec.role}:${b.spec.genericId ?? slug(`${b.spec.name}-${b.spec.volumeLitres}`)}`;
    const found = bufferGroups.get(key);
    if (found) found.count += 1;
    else
      bufferGroups.set(key, {
        item: BUFFER_ROLE_NAMES[b.spec.role],
        spec: `${b.spec.name}, holds ${litresText(b.spec.volumeLitres)} litres, ${vesselCaption(b.spec)}${
          b.spec.genericId !== null ? " — nominal, confirm with the chosen product" : " (entered by hand)"
        }`,
        count: 1,
      });
  }
  for (const [key, g] of [...bufferGroups.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    drafts.push({
      key,
      group: "plant",
      item: g.item,
      spec: g.spec,
      derivedQuantity: g.count,
      unit: "ea",
      basis: `${g.count} drawn on the plan.`,
      source: "plan",
      origin: "derived",
    });
  }

  // ---- radiators and valves ----------------------------------------------

  const radGroups = new Map<string, { spec: string; count: number }>();
  for (const r of radiators) {
    const key = `radiator:${r.spec.panelType}:${r.spec.heightMm}:${r.spec.lengthMm}`;
    const found = radGroups.get(key);
    if (found) found.count += 1;
    else
      radGroups.set(key, {
        spec:
          `${RADIATOR_PLAN_RANGE.brand} ${RADIATOR_PLAN_RANGE.range} ${radiatorLabel(r.spec)} mm ` +
          `(${PANEL_TYPE_ALIASES[r.spec.panelType]}), ${r.spec.depthMm} mm deep (nominal)`,
        count: 1,
      });
  }
  const radKeys = [...radGroups.keys()].sort();
  for (const key of radKeys) {
    const g = radGroups.get(key)!;
    drafts.push({
      key,
      group: "radiators",
      item: "Radiator",
      spec: g.spec,
      derivedQuantity: g.count,
      unit: "ea",
      basis: `${g.count} drawn on the plan.`,
      source: "plan",
      origin: "derived",
    });
  }
  if (radiators.length > 0) {
    drafts.push({
      key: "radiator-valves",
      group: "radiators",
      item: "TRV + lockshield valve pair",
      spec: `To suit ${settings.dropDiameterMm} mm tails`,
      derivedQuantity: radiators.length,
      unit: "pair",
      basis:
        `1 pair per radiator × ${radiators.length} radiators [RoT]. The room with the room thermostat ` +
        `may take lockshield-only (Benchmark "TRVs fitted/not required" [R]) — adjust here.`,
      source: "RoT",
      origin: "derived",
    });
    note(
      "radiators",
      "Brackets, air vent and blanking plug are not listed: they come in the radiator's box " +
        '("Everything required… within each radiator\'s packaging", Stelrad Spec Book 2022 [R]). ' +
        "Purmo's packaging has not been checked — confirm before loading.",
    );
  }

  // ---- pipe runs ---------------------------------------------------------

  const elbowsPlaced = fittings.filter((f) => f.spec.fitting === "elbow");
  const couplingsPlaced = fittings.filter((f) => f.spec.fitting === "coupling");

  const pipeRuns: PipeRunSummary[] = pipes.map((p) => {
    const points = p.geometry.points;
    const material = pipeMaterialOf(p.spec);
    const lengthM = runLengthM(points);
    const legs = runLegsM(points);
    const bends = runBends(points);
    const onThisStorey = (f: StockPlanObject) => f.storey === p.storey;
    const uncovered = bends.filter(
      (b) =>
        !elbowsPlaced.some(
          (e) =>
            onThisStorey(e) &&
            e.kind === "fitting" &&
            Math.hypot(e.geometry.x - b.x, e.geometry.y - b.y) <= FITTING_SNAP_M,
        ),
    ).length;
    const placedCouplings = couplingsPlaced.filter(
      (c) => onThisStorey(c) && c.kind === "fitting" && distanceToRunM(c.geometry, points) <= FITTING_SNAP_M,
    ).length;
    const joints = Math.max(0, ceilClean(lengthM / jointLengthM(material, p.spec.diameterMm, settings)) - 1);
    const spacing = material === "copper" ? CLIP_SPACING_HORIZONTAL_M[p.spec.diameterMm] : undefined;
    return {
      id: p.id,
      storey: p.storey,
      label: p.label,
      circuit: p.spec.circuit,
      diameterMm: p.spec.diameterMm,
      material,
      lengthM: clean(lengthM),
      legs: legs.length,
      bends: bends.length,
      uncoveredBends: uncovered,
      derivedCouplings: Math.max(0, joints - placedCouplings),
      clips: spacing === undefined ? null : clipsForRun(legs, spacing),
    };
  });

  // ---- pipe totals per material and diameter -----------------------------

  const extraOf = (e: ExtraPipe) => e.material ?? DEFAULT_PIPE_MATERIAL;
  const sizes = sizesOf([
    ...pipeRuns,
    ...(radiators.length > 0 && settings.dropPerRadiatorM > 0
      ? [{ material: settings.dropMaterial, diameterMm: settings.dropDiameterMm }]
      : []),
    ...settings.extraPipe.filter((e) => e.metres > 0).map((e) => ({ material: extraOf(e), diameterMm: e.diameterMm })),
  ]);
  const waste = 1 + settings.wastagePct / 100;

  const pipeTotals: PipeDiameterTotal[] = sizes.map(({ material, diameterMm: d }) => {
    const mine = pipeRuns.filter((r) => r.diameterMm === d && r.material === material);
    const flowM = mine.filter((r) => r.circuit === "flow").reduce((s, r) => s + r.lengthM, 0);
    const returnM = mine.filter((r) => r.circuit === "return").reduce((s, r) => s + r.lengthM, 0);
    const dropsM =
      d === settings.dropDiameterMm && material === settings.dropMaterial ? radiators.length * settings.dropPerRadiatorM : 0;
    const extraM = settings.extraPipe
      .filter((e) => e.diameterMm === d && extraOf(e) === material)
      .reduce((s, e) => s + e.metres, 0);
    const totalM = clean(flowM + returnM + dropsM + extraM);
    const orderM = clean(totalM * waste);
    const { stock } = stockPieces(material, d, orderM, settings);
    return {
      diameterMm: d,
      material,
      flowM: clean(flowM),
      returnM: clean(returnM),
      dropsM: clean(dropsM),
      extraM: clean(extraM),
      totalM,
      orderM,
      lengths: stock.pieces.reduce((s, p) => s + p.count, 0),
      stock,
    };
  });

  for (const t of pipeTotals) {
    const bought = stockPieces(t.material, t.diameterMm, t.orderM, settings);
    if (bought.warning && !warnings.includes(bought.warning)) warnings.push(bought.warning);
    const hasDrops = t.dropsM > 0;
    const parts = [
      `Plan: flow ${fmt(t.flowM)} m + return ${fmt(t.returnM)} m`,
      hasDrops
        ? `drops ${radiators.length} radiators × ${qty(settings.dropPerRadiatorM)} m = ${fmt(t.dropsM)} m [assumption]`
        : null,
      t.extraM > 0 ? `extra ${fmt(t.extraM)} m [assumption]` : null,
      `total ${fmt(t.totalM)} m`,
      `+${qty(settings.wastagePct)}% wastage [RoT] = ${fmt(t.orderM)} m`,
      bought.why,
    ].filter((p): p is string => p !== null);
    const coil = t.stock.unit === "coil";
    drafts.push({
      key: `pipe:${sizeKey(t.material, t.diameterMm)}`,
      group: "pipe",
      item:
        t.material === "copper"
          ? `${t.diameterMm} mm copper tube`
          : `${t.diameterMm} mm ${PIPE_MATERIAL_NAMES[t.material]} pipe${coil ? " (coil)" : ""}`,
      spec:
        t.material === "copper"
          ? `${qty(settings.stockLengthM)} m lengths, EN 1057 R250`
          : coil
            ? t.stock.pieces.map((p) => `${p.count} × ${qty(p.lengthM)} m coil`).join(" + ") || "Coils"
            : `${qty(settings.stockLengthM)} m lengths`,
      derivedQuantity: t.lengths,
      unit: coil ? "coil" : "length",
      basis: parts.join("; ") + ".",
      source: t.material === "copper" ? "I" : bought.source,
      measure: `${fmt(t.orderM)} m to order (${fmt(t.totalM)} m before wastage)`,
      origin: "derived",
    });
  }
  if (pipes.length === 0) warnings.push("No pipe runs on the plan — pipe, fittings and clips cover drops and extras only.");
  const storeys = new Set(input.objects.map((o) => o.storey));
  if (storeys.size > 1 && settings.extraPipe.every((e) => !(e.metres > 0))) {
    warnings.push(
      `The plan has objects on ${storeys.size} storeys but no extra metres for risers between them — add them under "Extra pipe".`,
    );
  }
  if (pipeRuns.some((r) => r.material === "multilayer")) {
    note(
      "pipe",
      "Multilayer is drawn at the copper size it replaces; its ranges are sized differently (16/20/26/32 mm). Order the matching size.",
    );
  }

  // ---- fittings ----------------------------------------------------------

  const joints = pipeJoints(pipes);
  const reducersPlaced = fittings.filter((f) => f.spec.fitting === "reducer");

  const placedGroups = new Map<string, { kind: FittingKind; spec: string; count: number }>();
  for (const f of fittings) {
    const s = f.spec;
    const size =
      s.diameterMm === null
        ? "any"
        : `${s.diameterMm}${s.fitting === "reducer" && s.reducedDiameterMm != null ? `x${s.reducedDiameterMm}` : ""}`;
    const key = `fitting:placed:${s.fitting}:${size}${s.material && s.material !== "copper" ? `:${s.material}` : ""}`;
    const found = placedGroups.get(key);
    if (found) found.count += 1;
    else
      placedGroups.set(key, {
        kind: s.fitting,
        spec:
          fittingSizeLabel(s) === null
            ? "Size not set on the plan"
            : s.fitting === "reducer" && s.reducedDiameterMm == null
              ? `${fittingSizeLabel(s)} — reduced size not set`
              : (fittingSizeLabel(s) as string),
        count: 1,
      });
  }
  for (const key of [...placedGroups.keys()].sort()) {
    const g = placedGroups.get(key)!;
    drafts.push({
      key,
      group: "fittings",
      item: FITTING_NAMES[g.kind],
      spec: g.spec,
      derivedQuantity: g.count,
      unit: "ea",
      basis: `${g.count} drawn on the plan.`,
      source: "plan",
      origin: "derived",
    });
  }

  for (const { material, diameterMm: d } of sizesOf(pipeRuns)) {
    const runs = pipeRuns.filter((r) => r.diameterMm === d && r.material === material);
    const label = pipeSizeLabel(d, material);
    const bends = runs.reduce((s, r) => s + r.bends, 0);
    const uncovered = runs.reduce((s, r) => s + r.uncoveredBends, 0);
    if (uncovered > 0) {
      drafts.push({
        key: `fitting:derived:elbow:${sizeKey(material, d)}`,
        group: "fittings",
        item: "Elbow (at plan bends)",
        spec: label,
        derivedQuantity: uncovered,
        unit: "ea",
        basis:
          `1 per bend of ${MIN_BEND_DEG}° or more in a ${label} run on the plan: ${bends} bends on ` +
          `${runs.length} run${runs.length === 1 ? "" : "s"}, less ${bends - uncovered} with an elbow ` +
          `already drawn within ${FITTING_SNAP_M * 1000} mm [I].` +
          (material === "copper" ? "" : " Plastic pipe can often be bent instead (with a bend support) — reduce where it will be."),
        source: "I",
        origin: "derived",
      });
    }
    const couplings = runs.reduce((s, r) => s + r.derivedCouplings, 0);
    if (couplings > 0) {
      const joint = jointLengthM(material, d, settings);
      drafts.push({
        key: `fitting:derived:coupling:${sizeKey(material, d)}`,
        group: "fittings",
        item: "Straight coupling (joints between lengths)",
        spec: label,
        derivedQuantity: couplings,
        unit: "ea",
        basis:
          `Per run: ceil(run length ÷ ${qty(joint)} m${material === "copper" ? "" : " coil"}) − 1, less couplings drawn on the run; ` +
          runs.map((r) => `${fmt(r.lengthM)} m → ${r.derivedCouplings}`).join(", ") +
          " [I].",
        source: "I",
        origin: "derived",
      });
    }
  }

  // Reducers where runs of different sizes meet, unless one is drawn there.
  const reducerCounts = new Map<string, { from: PipeDiameterMm; to: PipeDiameterMm; material: PlanPipeMaterial; count: number; at: string[] }>();
  let coveredReducers = 0;
  for (const joint of joints) {
    const needed = reducersAtJoint(joint);
    if (needed.length === 0) continue;
    const drawn = reducersPlaced.filter(
      (f) => f.storey === joint.storey && Math.hypot(f.geometry.x - joint.at.x, f.geometry.y - joint.at.y) <= 2 * FITTING_SNAP_M,
    ).length;
    const materials = new Set(joint.runs.map((r) => r.material));
    // Where materials meet too, the reducer is listed as copper and the joint
    // is named: a plastic-to-copper adaptor may do both jobs.
    const material: PlanPipeMaterial = materials.size === 1 ? [...materials][0]! : "copper";
    needed.slice(drawn).forEach((r) => {
      const key = `${r.from}x${r.to}${material === "copper" ? "" : `:${material}`}`;
      const found = reducerCounts.get(key) ?? { ...r, material, count: 0, at: [] };
      found.count += 1;
      found.at.push(`(${fmt(joint.at.x)}, ${fmt(joint.at.y)})${materials.size > 1 ? " where copper and plastic meet — an adaptor may do both jobs" : ""}`);
      reducerCounts.set(key, found);
    });
    coveredReducers += Math.min(drawn, needed.length);
  }
  for (const [key, r] of [...reducerCounts.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    drafts.push({
      key: `fitting:derived:reducer:${key}`,
      group: "fittings",
      item: "Reducer (where sizes meet)",
      spec: `${r.from} × ${r.to} mm${r.material === "copper" ? "" : ` ${PIPE_MATERIAL_NAMES[r.material]}`}`,
      derivedQuantity: r.count,
      unit: "ea",
      basis:
        `1 for each smaller run meeting a larger one of the same circuit — end to end, or branching off it — within ` +
        `${FITTING_SNAP_M * 1000} mm on the plan, less reducers drawn there [I]: ${r.count} at ${r.at.join(", ")}` +
        `${coveredReducers > 0 ? `; ${coveredReducers} joint${coveredReducers === 1 ? " is" : "s are"} covered by a drawn reducer` : ""}. ` +
        "At a branch a reducing tee does the same job — count one or the other.",
      source: "I",
      origin: "derived",
    });
  }

  if (radiators.length > 0 && settings.dropPerRadiatorM > 0) {
    const dropLabel = pipeSizeLabel(settings.dropDiameterMm, settings.dropMaterial);
    drafts.push({
      key: `fitting:derived:drop-elbow:${sizeKey(settings.dropMaterial, settings.dropDiameterMm)}`,
      group: "fittings",
      item: "Elbow (radiator drops)",
      spec: dropLabel,
      derivedQuantity: radiators.length * 2,
      unit: "ea",
      basis:
        `2 per radiator (flow and return tails turn up to the valve) × ${radiators.length} radiators [I]. ` +
        "The plan cannot show these turns; set to 0 where tees feed the valves directly.",
      source: "I",
      origin: "derived",
    });
  }
  note("fittings", "Tees are not derived: draw them on the plan. Reducers are derived where runs of different sizes meet.");

  // ---- clips -------------------------------------------------------------

  for (const { material, diameterMm: d } of sizesOf(pipeRuns)) {
    const runs = pipeRuns.filter((r) => r.diameterMm === d && r.material === material);
    if (material !== "copper") {
      const text =
        `No clip spacing on record for ${PIPE_MATERIAL_NAMES[material]} pipe — plastic is clipped closer than copper, per its manufacturer ` +
        "(research §2). Count its clips on site or add a manual line.";
      if (!warnings.includes(text)) warnings.push(text);
      continue;
    }
    const spacing = CLIP_SPACING_HORIZONTAL_M[d];
    if (spacing === undefined) {
      warnings.push(
        `No clip spacing on record for ${d} mm pipe (research transcribed 15, 22, 28 and 35 mm only) — count ${d} mm clips on site or add a manual line.`,
      );
      continue;
    }
    const clips = runs.reduce((s, r) => s + (r.clips ?? 0), 0);
    drafts.push({
      key: `clips:${d}`,
      group: "fixings",
      item: "Pipe clip",
      spec: `${d} mm`,
      derivedQuantity: clips,
      unit: "ea",
      basis:
        `Max ${qty(spacing)} m apart on horizontal runs (Mueller Table 10 [R]); per run ` +
        `Σ ceil(leg ÷ ${qty(spacing)} m) + 1, a clip at each end and bend [I]: ` +
        runs.map((r) => `${fmt(r.lengthM)} m run → ${r.clips}`).join(", ") +
        ". Plan runs only — drops and extras are not clipped here.",
      source: "I",
      origin: "derived",
    });
  }

  // ---- insulation --------------------------------------------------------

  if (settings.insulationCoveragePct > 0) {
    // By outside diameter: lagging fits the pipe's outside, whatever it is made of.
    const byD = new Map<PipeDiameterMm, number>();
    for (const t of pipeTotals) byD.set(t.diameterMm, (byD.get(t.diameterMm) ?? 0) + t.totalM);
    for (const d of [...byD.keys()].sort(byDiameter)) {
      const totalM = clean(byD.get(d)!);
      const metres = ceilClean(totalM * (settings.insulationCoveragePct / 100) * waste);
      if (metres <= 0) continue;
      drafts.push({
        key: `insulation:${d}`,
        group: "insulation",
        item: "Pipe insulation",
        spec: `To suit ${d} mm pipe, 19–25 mm wall`,
        derivedQuantity: metres,
        unit: "m",
        basis:
          `${qty(settings.insulationCoveragePct)}% of ${fmt(totalM)} m of ${d} mm pipe ` +
          `+${qty(settings.wastagePct)}% wastage, rounded up to whole metres. Common practice lags every ` +
          "primary [RoT]; the legal minimum is primaries outside heated space, all DHW primaries and 1 m " +
          "from the cylinder (AD L 4.24–4.26 [R]; Table 4.4: ≤25 mm ID → 10 mm, ≤50 mm → 15 mm at λ ≤ 0.025). " +
          "External runs need UV-resistant or clad lagging.",
        source: "RoT",
        origin: "derived",
      });
    }
  }

  // ---- system water volume -----------------------------------------------

  const systemVolume = computeSystemVolume({
    pipeTotals,
    radiators: radiators.length,
    heatPumps: heatPumpCount,
    cylinders,
    buffers,
    designCylinderName: cylinder && cylinderName ? cylinderName : null,
    extras: settings.volumeExtras,
  });
  const volumeFloor = systemVolume.notIncluded.length > 0;
  if (volumeFloor) {
    note(
      "water-treatment",
      `System water volume ${fmt(systemVolume.knownLitres, 1)} L from what is on record — NOT including ${systemVolume.notIncluded.join("; ")}. ` +
        "Doses worked from it are a minimum: add the missing litres under Allowances → system water.",
    );
  } else if (systemVolume.lines.length > 0) {
    note("water-treatment", `System water volume ${fmt(systemVolume.knownLitres, 1)} L — see the breakdown.`);
  }

  let glycol: StockReport["glycol"] = null;
  if (settings.glycolConcentrationPct > 0) {
    const litres = ceilClean((systemVolume.knownLitres * settings.glycolConcentrationPct) / 100);
    glycol = { concentrationPct: settings.glycolConcentrationPct, litres };
    drafts.push({
      key: "glycol",
      group: "water-treatment",
      item: "Glycol (propylene)",
      spec: `${qty(settings.glycolConcentrationPct)}% by volume`,
      derivedQuantity: litres,
      unit: "L",
      basis:
        `${qty(settings.glycolConcentrationPct)}% × ${fmt(systemVolume.knownLitres, 1)} L of system water` +
        `${volumeFloor ? " (known parts only — a minimum)" : ""} = ${fmt((systemVolume.knownLitres * settings.glycolConcentrationPct) / 100, 2)} L, ` +
        "rounded up to whole litres [I]. EST's toolkit: 25% protects to about −10 °C, and glycol cuts the heat pump's " +
        "output (its example: 7.01 → 6.42 kW) [R] — check the unit's capacity at this mix.",
      source: "I",
      origin: "derived",
    });
    note("water-treatment", "A glycol system does not need anti-freeze valves — exclude them if they are listed.");
  }
  note(
    "water-treatment",
    "Inhibitor and cleaner: give each a \"per 100 L of system water\" rule in the stock catalogue, from the product's label, and it is dosed from the volume.",
  );

  // ---- the organisation's catalogue -------------------------------------

  const totalPipeM = clean(pipeTotals.reduce((s, t) => s + t.totalM, 0));
  for (const entry of input.catalogue ?? []) {
    if (!entry.active || entry.rule === null) continue;
    const { kind, quantity: per } = entry.rule;
    let derived: number;
    let why: string;
    switch (kind) {
      case "per-job":
        derived = per;
        why = `${qty(per)} per job`;
        break;
      case "per-radiator":
        derived = ceilClean(per * radiators.length);
        why = `${qty(per)} per radiator × ${radiators.length}`;
        break;
      case "per-heat-pump":
        derived = ceilClean(per * heatPumpCount);
        why = `${qty(per)} per heat pump × ${heatPumpCount}`;
        break;
      case "per-metre-of-pipe":
        derived = ceilClean(per * totalPipeM);
        why = `${qty(per)} per metre × ${fmt(totalPipeM)} m of pipe (before wastage), rounded up`;
        break;
      case "per-100-litres-of-system-water":
        derived = ceilClean((per * systemVolume.knownLitres) / 100);
        why =
          `${qty(per)} per 100 L × ${fmt(systemVolume.knownLitres, 1)} L of system water` +
          `${volumeFloor ? " (known parts only — a minimum)" : ""}, rounded up`;
        if (systemVolume.knownLitres <= 0) {
          warnings.push(`"${entry.name}" is dosed per 100 L of system water, but no system water is on record — it comes to 0.`);
        }
        break;
    }
    drafts.push({
      key: `catalogue:${entry.id}`,
      group: entry.category,
      item: entry.name,
      spec: entry.code ? `Code ${entry.code}` : "",
      derivedQuantity: derived,
      unit: entry.unit,
      basis: `Catalogue rule: ${why}.${entry.basis ? ` ${entry.basis}` : ""}`,
      source: entry.source ?? "catalogue",
      origin: "catalogue",
    });
  }

  // ---- manual lines ------------------------------------------------------

  for (const m of adjustments.manualLines) {
    drafts.push({
      key: `manual:${m.id}`,
      group: m.group,
      item: m.item,
      spec: m.spec,
      derivedQuantity: null,
      unit: m.unit,
      basis: m.note ? `Added by hand: ${m.note}` : "Added by hand for this job.",
      source: "manual",
      origin: "manual",
    });
  }

  // ---- adjustments -------------------------------------------------------

  const manualQuantities = new Map(adjustments.manualLines.map((m) => [`manual:${m.id}`, m.quantity]));
  const lines: StockLine[] = drafts.map((d) => {
    const adj = adjustments.lines[d.key];
    const base = d.derivedQuantity ?? manualQuantities.get(d.key) ?? 0;
    const overridden = adj?.quantity !== undefined;
    return {
      ...d,
      quantity: overridden ? (adj!.quantity as number) : base,
      overridden,
      excluded: adj?.excluded === true,
      note: adj?.note?.trim() ? adj.note.trim() : null,
    };
  });
  const keys = new Set(lines.map((l) => l.key));
  const unmatchedAdjustmentKeys = Object.keys(adjustments.lines).filter((k) => !keys.has(k)).sort();

  const groups: StockGroupView[] = STOCK_GROUPS.map((group) => ({
    group,
    title: STOCK_GROUP_TITLES[group],
    lines: lines.filter((l) => l.group === group),
    notes: notes[group] ?? [],
  })).filter((g) => g.lines.length > 0 || g.notes.length > 0);

  return {
    settings,
    groups,
    lines: groups.flatMap((g) => g.lines),
    pipeRuns,
    pipeTotals,
    systemVolume,
    glycol,
    counts: {
      radiators: radiators.length,
      heatPumps: heatPumps.length,
      pipeRuns: pipes.length,
      cylinders: cylinders.length,
      buffers: buffers.length,
    },
    warnings,
    unmatchedAdjustmentKeys,
  };
}

// ----------------------------------------------------- system water volume --

interface SystemVolumeInput {
  readonly pipeTotals: readonly PipeDiameterTotal[];
  readonly radiators: number;
  readonly heatPumps: number;
  readonly cylinders: readonly Extract<StockPlanObject, { kind: "cylinder" }>[];
  readonly buffers: readonly Extract<StockPlanObject, { kind: "buffer" }>[];
  /** The design decisions' cylinder, when one is named and none is drawn. */
  readonly designCylinderName: string | null;
  readonly extras: readonly VolumeExtra[];
}

/**
 * The water the heating circuit holds, part by part. Pipe is counted at its
 * installed metres (plan + drops + extras, BEFORE wastage — waste is not
 * filled) × its bore per metre; cylinder coils and buffers at their drawn
 * figures; anything else only as a person types it. A part on the job with
 * no figure on record is a line with null litres, and is named in
 * `notIncluded`.
 */
export function computeSystemVolume(input: SystemVolumeInput): SystemVolume {
  const lines: SystemVolumeLine[] = [];

  for (const t of input.pipeTotals) {
    if (!(t.totalM > 0)) continue;
    const label = pipeSizeLabel(t.diameterMm, t.material);
    const bore = boreLitresPerMetre(t.diameterMm, t.material);
    lines.push(
      bore
        ? {
            key: `pipe:${sizeKey(t.material, t.diameterMm)}`,
            component: `${label} pipe, ${fmt(t.totalM)} m`,
            litres: clean(t.totalM * bore.litresPerM),
            basis: `${fmt(t.totalM)} m installed (plan + drops + extras, before wastage) × ${bore.litresPerM} L/m — ${bore.basis} [${bore.label}].`,
            source: bore.label,
          }
        : {
            key: `pipe:${sizeKey(t.material, t.diameterMm)}`,
            component: `${label} pipe, ${fmt(t.totalM)} m`,
            litres: null,
            basis: `No bore volume on record for ${label} pipe (walls differ by make) — add its litres from the pipe's datasheet.`,
            source: "assumption",
          },
    );
  }

  if (input.radiators > 0) {
    lines.push({
      key: "radiators",
      component: `Radiators, ${input.radiators}`,
      litres: null,
      basis:
        "Not included: the radiator catalogue carries no water content. Add each radiator's litres from its datasheet.",
      source: "assumption",
    });
  }

  if (input.heatPumps > 0) {
    lines.push({
      key: "heat-pump",
      component: `Heat pump${input.heatPumps > 1 ? `s, ${input.heatPumps}` : ""}`,
      litres: null,
      basis: "Not included: no heat pump's internal water content is on record. Add it from the unit's datasheet.",
      source: "assumption",
    });
  }

  for (const c of input.cylinders) {
    const known = c.spec.coilVolumeLitres;
    lines.push({
      key: `cylinder-coil:${c.id}`,
      component: `Cylinder coil — ${c.spec.name}${c.label ? ` (${c.label})` : ""}`,
      litres: known,
      basis:
        known === null
          ? "Not included: no coil volume entered for this cylinder. Add it on the plan from the cylinder's datasheet."
          : c.spec.genericId !== null
            ? `${litresText(known)} L — the coil capacity printed for this nominal size (Kingspan Aerocyl HP range, manual 1054342 AUG 2023 v6, p. 15 [R]); confirm with the chosen cylinder.`
            : `${litresText(known)} L, entered on the plan.`,
      source: known === null ? "assumption" : c.spec.genericId !== null ? "assumption" : "plan",
    });
  }
  if (input.cylinders.length === 0 && input.designCylinderName) {
    lines.push({
      key: "cylinder-coil:design",
      component: `Cylinder coil — ${input.designCylinderName}`,
      litres: null,
      basis: "Not included: the design decisions do not record a coil volume. Draw the cylinder on the plan with its coil volume, or add it here.",
      source: "assumption",
    });
  }

  for (const b of input.buffers) {
    lines.push({
      key: `buffer:${b.id}`,
      component: `${BUFFER_ROLE_NAMES[b.spec.role]} — ${b.spec.name}${b.label ? ` (${b.label})` : ""}`,
      litres: b.spec.volumeLitres,
      basis:
        b.spec.genericId !== null
          ? `${litresText(b.spec.volumeLitres)} L — the water this nominal size holds (Kingspan Aerocyl buffer range, manual 1054342 AUG 2023 v6, p. 14 [R]); confirm with the chosen vessel.`
          : `${litresText(b.spec.volumeLitres)} L, entered on the plan.`,
      source: b.spec.genericId !== null ? "assumption" : "plan",
    });
  }

  input.extras.forEach((e, i) => {
    lines.push({
      key: `extra:${i}`,
      component: e.label,
      litres: e.litres,
      basis: "Entered for this job.",
      source: "manual",
    });
  });

  const knownLitres = clean(lines.reduce((s, l) => s + (l.litres ?? 0), 0));
  // A radiator or heat pump line is not missing when somebody has typed its litres as an extra.
  const typed = input.extras.map((e) => e.label.toLowerCase());
  const covered = (l: SystemVolumeLine) =>
    (l.key === "radiators" && typed.some((t) => t.includes("radiator"))) ||
    (l.key === "heat-pump" && typed.some((t) => t.includes("heat pump") || t.includes("outdoor unit")));
  const notIncluded = lines.filter((l) => l.litres === null && !covered(l)).map((l) => l.component);
  return { lines, knownLitres, notIncluded };
}
